/** Dragging in the outline (plan §5, pointer only — Alt+arrows and the rows' menus are the
 *  keyboard path): what a drop means and what a screen reader hears, in the tour's words
 *  (the records outline does the dragging, `RecordOutline`'s `drag`). Pure. */
import type { OutlineRow } from './outlineRows';

export type DropOutcome =
  | { kind: 'move-day'; dayId: number; toIndex: number }
  | { kind: 'move-slot'; slotId: number; toDayId: number }
  /** A stop dropped on its own day: stops are ordered by their time. */
  | { kind: 'same-day'; slotId: number; dayId: number }
  | null;

/** The outcome of dropping `from` on `over`. */
export function dropOutcome(from: OutlineRow | undefined, over: OutlineRow | undefined, rows: readonly OutlineRow[]): DropOutcome {
  if (!from || !over || from.removed || over.removed) return null;
  if (from.kind === 'day') {
    const host = over.kind === 'day' ? over : rows.find((r) => r.kind === 'day' && r.id === over.dayId && !r.removed);
    const toIndex = host?.kind === 'day' ? host.index : null;
    if (toIndex === null || toIndex === undefined || from.index === toIndex) return null;
    return { kind: 'move-day', dayId: from.id, toIndex };
  }
  const toDayId = over.kind === 'day' ? over.id : over.dayId;
  return toDayId === from.dayId ? { kind: 'same-day', slotId: from.id, dayId: toDayId } : { kind: 'move-slot', slotId: from.id, toDayId };
}

export interface DragNames {
  /** "Day 3 · Galway", "10:00 Cliffs of Moher". */
  nameOf: (row: OutlineRow) => string;
  /** 1-based day number of a day id. */
  dayNumberOf: (dayId: number) => number;
}

export const DRAG_INSTRUCTIONS =
  'To move a stop to another day, drop it on that day or on one of its stops; drop a day on another day to put it in that place. ' +
  'From the keyboard: Alt with an arrow moves a day, and a row’s actions menu moves a stop.';

/** What a screen reader hears while dragging — the rows' names, never raw ids. */
export function dragAnnouncement(
  phase: 'start' | 'over' | 'end' | 'cancel',
  from: OutlineRow | undefined,
  over: OutlineRow | undefined,
  rows: readonly OutlineRow[],
  names: DragNames,
): string | undefined {
  if (!from) return undefined;
  const name = names.nameOf(from);
  if (phase === 'start') return `Picked up ${name}. ${from.kind === 'slot' ? 'Drop it on another day to move this stop.' : 'Drop it on another day to put it in that place.'}`;
  if (phase === 'cancel') return `${name} was not moved.`;
  const outcome = dropOutcome(from, over, rows);
  const done = phase === 'end';
  if (!outcome) return done ? `${name} was not moved.` : undefined;
  if (outcome.kind === 'move-day') return `${name} ${done ? 'moved' : 'moves'} to Day ${outcome.toIndex + 1}.`;
  if (outcome.kind === 'move-slot') return `${name} ${done ? 'moved' : 'moves'} to Day ${names.dayNumberOf(outcome.toDayId)}.`;
  return `${name} is already in Day ${names.dayNumberOf(outcome.dayId)} — stops are in time order${done ? '; it was not moved' : ''}.`;
}
